import CustomZObject from '../CustomZObject.js';
import SDFOperations from "zobject/custom/sdf/SDFOperations.js";
import {isNumber} from "utils/IsNumber.js"
import {sdf_union} from '../../definitions/gl/sdf/sdf.js';
import {gl_uniform, gl_variable} from '../../definitions/gl/variables.js';
import {gl_sdf_scene, gl_sdf} from '../../definitions/gl/sdf/scene.js';
import {gl_vec3, gl_vec2} from '../../definitions/gl/vec.js';
import {gl_mat3} from '../../definitions/gl/matrix.js';
import {getPositions, getDimensions, getCorner, getRotation, is3DType} from './types.js';
import HandlesMouse from './HandlesMouse.js';
import SDFIndex from './SDFIndex.js';
import {compose} from "utils/compose.js"
import HandlesArgs from './HandlesArgs.js';
import Transformable from './Transformable.js';

const MIN_SDF_VALUE = 0.005;

export default class SDFManager extends compose(
    SDFIndex,
    Transformable,
    HandlesArgs,
    HandlesMouse,
    SDFOperations)(
    CustomZObject) {
    constructor(zobject) {
        super(zobject);

        zobject.skipHotReload = true;
        this.skipHotReload = true;
        this.dimensionVecs = {};
        this.positionVecs = {};
        this.indexedUniforms = {};
        this.uniformValues = {};
        this.positionUniforms = [];
        this.dimensionsUniforms = [];
        this.cornerUniforms = [];
        this.glSDFs = [];

        this.positions = [];
        this.dimensions = [];
        this.corners = [];

        this.newInlet("SDF List", ([list]) => this.onSDFList(list));
        this.newInlet("mouse down", ([coords]) => this.onMouseDown(coords));
        this.newInlet("mouse up", ([coords]) => this.onMouseUp(coords));
        this.newInlet("mouse move", ([coords]) => this.onMouseMove(coords));
        this.newInlet("slider values", ([values]) => this.onSliderValues(values));
        this.newInlet("size", ([coords]) => {
            this.size = coords;
        });

        this.newInlet("operations", ([op]) => this.onOperation(op));
        this.newInlet("SDF Morpher", ([op]) => {
            this.sdfMorpher = op;
            this.onSDFList(this.lastSDFs, true);
        });

        this.newInlet("SDF Offset", ([op]) => {
            this.sdfOffset = op;
            this.onSDFList(this.lastSDFs, true);
        });

        this.newInlet("SDF Operation List", ([op]) => {
            this.onSDFOperationList(op);
        });

        this.newOutlet("Merged GL SDF");
        this.newOutlet("GL SDF List");
        this.newOutlet("GL SDF Evaluators");
        this.newOutlet("vec2(sdf,index)");

        this.newOutlet("Index Clicked");
        this.newOutlet("On Slider Change");
        this.newOutlet("On Slider SDF Change");
        this.newInlet("transformation matrix", ([mat]) => this.onTransformationMatrix(mat));
        this.newInlet("transformation matrix list", ([mat]) => this.onTransformationList(mat));
        this.newOutlet("Transformed Positions");

        this.setup();
    }

    hotReload() {
        if (this.lastSDFs) {
            this.onSDFList(this.lastSDFs);

            if (this.lastSent) {
                for (let key in this.lastSent) {
                    this.sendMessage(key, this.lastSent[key]);
                }
            }
        }
    }

    onSDFList(sdfList, forceRecompile=false) {
        // the format of each element in the sdf list is as follows:
        // [TYPE, [x_position, y_position, (z_position)], [width, (height), (depth)]]

        if (!sdfList) {
            return;
        }

        sdfList = validateSDFList(sdfList);

        // given the sdf list sort by id placement
        sdfList = sortByCopies(sdfList);
        // create a distinct uniform for each variable

        if (sdfList.every(x => x.copyId)) {
            // they're all copies so we need to wait for the actual SDFs
            return;
        }

        // keep track of the last inputed sdfs list
        this.lastSDFs = [... sdfList];

        if (!forceRecompile && this.storedSDFs && sdfList.length === this.storedSDFs.length) {
            // then we're just updating
            this.updateUniforms(sdfList);
            this.sendTransformedPositions();
            // console.log('sending stored sdfs');
            // this.sendMessage("GL SDF List", this.storedSDFs);
            return;
        }

        let sdfs = [];
        let sdfEvaluators = [];
        let uniforms = {};
        
        for (let args of sdfList) {
            let id = args.id;
            let index = this.getIndexForId(id);
            if (isCopy(args)) {
                let {sdf, sdfEvaluator, ... rest} = this.handleSDFCopy(index, args, sdfList);
                sdfs.push(sdf);
                sdfEvaluators.push(sdfEvaluator);
                uniforms = {
                    ... uniforms,
                    ... rest.uniforms
                };
                continue;
            }

            let [type] = args;
            let corner = getCorner(args);
            let rotation = getRotation(args);
            let positionExp = this.getPosition(index, args);
            let dimensionsExp = this.getDimensions(index, args);
            let cornerExp = this.getCorner(index, corner);
            let rotationExp = this.getRotation(index, rotation);
            let repeatsExp = this.getRepeats(index, args);

            this.indexedUniforms[index] = {
                ... this.indexedUniforms[index],
                ... positionExp.uniforms,
                ... dimensionsExp.uniforms,
                ... cornerExp.uniforms,
                ... rotationExp.uniforms,
                ... repeatsExp.uniforms,
            };

            // keep track of all the uniforms being made (so we can output through the outlets)
            uniforms = {
                ... uniforms,
                ... this.indexedUniforms[index]
            };
            positionExp.uniforms = this.indexedUniforms[index];
            dimensionsExp.uniforms = this.indexedUniforms[index];
            cornerExp.uniforms = this.indexedUniforms[index];
            rotationExp.uniforms = this.indexedUniforms[index];
            repeatsExp.uniforms = this.indexedUniforms[index];
            let _index = index;

            let sdf = this.glSDFs[index] || gl_sdf(
                this.zObject,
                () => type,
                () => dimensionsExp,
                () => cornerExp,
                () => {
                    let ret= this.transformationMatrices[_index] || this.transformationMatrix
                    return ret;
                },
                () => rotationExp,
                () => _index,
                () => repeatsExp
            );
            this.glSDFs[index] = sdf;

            let _sdf = sdf([positionExp])[1];
            let variables = {};
            let varName = this.name("singleSDF", index);
            variables[varName] = {
                code: '(('+ _sdf.code+ ').x)',
                type: "float"
            };
                    
            _sdf = {
                ... _sdf,
                variables,
                code: varName //'(('+ _sdf.code+ ').x)',
            };

            sdfs.push(_sdf);
            sdfEvaluators.push(sdf([positionExp])[0]);
        }

        this.zObject.needsHotReload = false;

        if (sdfs.length === 0) {
            return;
        }

        this.storedSDFs = sdfs;

        let is3D = is3DType(sdfList[0] ? sdfList[0][0] : 'box');
        let type = is3D ? 'vec3' : "vec2";
        let scene = gl_sdf_scene(
            this.zObject,
            () => type,
            () => this.sdfMorpher,
            () => this.sdfOffset,
            () => this.sdfOperationsList);
        let _scene = scene([sdfEvaluators]);

        let uv = is3D ?
            'vec3((1.0*gl_FragCoord.xy-vec2(width, height))/height, 0.0)' :
            '(1.0*gl_FragCoord.xy-vec2(width, height))/height'; 
            
        let union = _scene.code(uv); //sdf_union(sdfs.map(x => x.code));

        // now construct the data to pass on
        let functions = _scene.functions;
        for (let sdf of sdfs) {
            functions = {
                ... functions,
                ... sdf.functions
            };
            
        }
        let data = {
            code: '(('+ union + ').x)',
            functions,
            uniforms: {
                ... uniforms,
                ... this.transformationMatrix.uniforms
            },
            variables: {}
        };

        let mergedIndex  = {
            code: union,
            functions,
            uniforms: {
                ... uniforms,
                ... this.transformationMatrix.uniforms
            },
            variables: {}
        };

        this.lastSent = {
            "Merged GL SDF": data,
            "vec2(sdf,index)": mergedIndex,
            "GL SDF List": sdfs,
            "GL SDF Evaluators": _scene
        };

        this.sendMessage("Merged GL SDF", data);
        this.sendMessage("vec2(sdf,index)", mergedIndex);
        this.sendMessage("GL SDF List", sdfs);
        this.sendMessage("GL SDF Evaluators", _scene);

        this.zObject.needsHotReload = false;

        this.sendTransformedPositions();
    }

    updateUniforms(sdfList) {
        this.zObject.needsHotReload = false;
        window.stopRender = true;
        for (let args of sdfList) {
            this.updateUniformsForSDF(args, sdfList);
        }
    }

}

const validateSDF = (type, pos, dim) => {
    if (type !== "rect" && type !== "sphere" && type !== "circle") {
        return false;
    }

    if (!Array.isArray(pos) || !Array.isArray(dim)) {
        return false;
    }
    return true;
};


// copies need to happen after the actual objects...
const sortByCopies = (x) => {
    let notCopies = x.filter(y => !isCopy(y));
    let copies = x.filter(y => isCopy(y));

    return [... notCopies, ...copies];
};

const isCopy = (x) => {
    return !Array.isArray(x);
};

const validateSDFList = (list) => {
    if (list.some(x => typeof x === "number")) {
        list = list.filter(x => !isNumber(x));
    }
    
    for (let i=0; i < list.length; i++) {
        let el = list[i];
        if (typeof el === "string") {
            // then we need to convert
            let tokens = el.split(" ").map(
                x => isNumber(x) ? parseFloat(x) : x);
            list[i] = tokens;
        } 
        if (!list[i].id) {
            list[i].id = i;
        }
    }
    return list;
};
